import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { message, Icon, Select, Button, Card, Divider } from 'antd';
import { getAllCategories } from '../../utils/services/categoriesAPI';
import { exportCategoryToZip } from '../../utils/bulkDocumentExportService';
import './styles.css';

const { Option } = Select;

const BulkDocumentExportPage = () => {
  const [categories, setCategories] = useState([]);
  const [sectionCategories, setSectionCategories] = useState([]);
  const [selectedDocumentCategoryId, setSelectedDocumentCategoryId] = useState(null);
  const [selectedSectionCategoryIds, setSelectedSectionCategoryIds] = useState([]);
  const [exportProgress, setExportProgress] = useState(null);
  const [isExporting, setIsExporting] = useState(false);

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const [templateRes, elementRes] = await Promise.all([
          getAllCategories(false, 'template'),
          getAllCategories(false, 'element'),
        ]);
        if (templateRes?.statusCode === 507 && templateRes?.data) {
          setCategories(templateRes.data);
          if (templateRes.data.length === 1) setSelectedDocumentCategoryId(templateRes.data[0].id);
        }
        if (elementRes?.statusCode === 507 && elementRes?.data) {
          setSectionCategories(elementRes.data);
        }
      } catch (e) {
        console.error(e);
        message.error('Failed to load categories');
      }
    };
    fetchCategories();
  }, []);

  const handleExport = useCallback(async () => {
    if (!selectedDocumentCategoryId) {
      message.error('Please select a document category');
      return;
    }
    if (!selectedSectionCategoryIds?.length) {
      message.error('Please select at least one section category');
      return;
    }
    setIsExporting(true);
    setExportProgress({ stage: 'fetching-documents', message: 'Starting export...' });
    try {
      const blob = await exportCategoryToZip(
        selectedDocumentCategoryId,
        selectedSectionCategoryIds,
        p => setExportProgress(p || null),
      );
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'export.zip';
      a.click();
      URL.revokeObjectURL(url);
      message.success('Export downloaded');
    } catch (err) {
      console.error(err);
      message.error(err?.message || 'Export failed');
    } finally {
      setIsExporting(false);
      setExportProgress(null);
    }
  }, [selectedDocumentCategoryId, selectedSectionCategoryIds]);

  const canExport = selectedDocumentCategoryId && selectedSectionCategoryIds?.length > 0 && !isExporting;

  return (
    <div className="bulk-document-export-page">
      <Card title="Bulk Document Export" style={{ marginBottom: 20 }}>
        <p style={{ marginBottom: 16, color: '#666' }}>
          <Link to="/bulk-document-import">Bulk import</Link> documents and sections from a ZIP.
        </p>

        <div style={{ marginBottom: 20 }}>
          <label style={{ display: 'block', marginBottom: 8, fontWeight: 'bold' }}>Document Category</label>
          <Select
            style={{ width: '100%', maxWidth: 400 }}
            placeholder="Category for documents (templates)"
            value={selectedDocumentCategoryId}
            onChange={setSelectedDocumentCategoryId}
            disabled={isExporting}
          >
            {categories.map(c => (
              <Option key={c.id} value={c.id}>
                {c.name || `Category ${c.id}`}
              </Option>
            ))}
          </Select>
        </div>

        <div style={{ marginBottom: 20 }}>
          <label style={{ display: 'block', marginBottom: 8, fontWeight: 'bold' }}>Section Categories</label>
          <Select
            mode="multiple"
            style={{ width: '100%', maxWidth: 400 }}
            placeholder="Categories for standalone blocks (sections, type: element)"
            value={selectedSectionCategoryIds}
            onChange={setSelectedSectionCategoryIds}
            disabled={isExporting}
          >
            {sectionCategories.map(c => (
              <Option key={c.id} value={c.id}>
                {c.name || `Category ${c.id}`}
              </Option>
            ))}
          </Select>
        </div>

        <p style={{ marginBottom: 16, color: '#666' }}>
          Export includes variables, images, relationships, sections from the selected section categories, and documents from the selected document category, in the recommended folder structure for re-import.
        </p>

        <Divider />

        <div style={{ marginBottom: 20, display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <Button type="primary" onClick={handleExport} disabled={!canExport}>
            {isExporting ? (
              <>
                <Icon type="loading" style={{ marginRight: 6 }} />
                Exporting...
              </>
            ) : (
              'Export'
            )}
          </Button>
          {exportProgress && (
            <span style={{ color: '#666' }}>
              {exportProgress.message}
              {exportProgress.current != null && exportProgress.total != null
                ? ` (${exportProgress.current}/${exportProgress.total})`
                : ''}
            </span>
          )}
        </div>
      </Card>
    </div>
  );
};

export default BulkDocumentExportPage;
